import { useQuery } from '@tanstack/react-query';
import {
  ActiveSourcingChannelDetails,
  SourcingOpeningChannelsResponse,
  SourcingService,
} from 'api-clients/monolith';

import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

/**
 * Returns the most recently automation-triggered ongoing campaign for the
 * current opening. Derived from the same channels-list query used by
 * `ChannelsSection`, so React Query dedupes the request.
 */
export function useLastAutomationChannel():
  | ActiveSourcingChannelDetails
  | undefined {
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();

  // Fire the channels-list fetch BEFORE the suspending `useOpeningDetails`
  // call below so it doesn't wait on the funnel-details API.
  const { data: response } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.list,
      openingSlug,
      hiringGoalId,
      displayPrefs.showAggregateHires,
      displayPrefs.showAggregateSpend,
      startDate,
      endDate,
      filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug,
        hiringGoalId,
        displayPrefs.showAggregateHires,
        displayPrefs.showAggregateSpend,
        startDate,
        endDate,
        filterRange,
      ),
  });

  const { hiringGoal } = useOpeningDetails();
  const isCurrentHiringGoal = hiringGoal?.is_current ?? false;

  if (!response) return undefined;

  return selectLastAutomationChannel(response, isCurrentHiringGoal);
}

function selectLastAutomationChannel(
  response: SourcingOpeningChannelsResponse,
  isCurrentHiringGoal: boolean,
): ActiveSourcingChannelDetails | undefined {
  const ongoing = response.active_channels.filter(
    c => c.status === 'active' || c.status === 'stopped',
  );
  const otherChannel =
    isCurrentHiringGoal && response.other_channel
      ? [response.other_channel]
      : [];

  return [...ongoing, ...otherChannel]
    .filter(c => c.automation_triggered_at)
    .sort(
      (a, b) =>
        Date.parse(String(b.automation_triggered_at)) -
        Date.parse(String(a.automation_triggered_at)),
    )[0];
}
